import Image from "next/image";
import type { ComponentPropsWithoutRef } from "react";

import { PRODUCT_DEF } from "@/lib/product";
import { cn } from "@/lib/utils";

const ATRIAM_ICON = "/icons/construction/pwa-icon-192.png";

/** The approved Atriam Ionic column, shared with native and install icons. */
export function AtriamMark({
  className,
  title = "Atriam",
  decorative = false,
}: {
  className?: string;
  /** Accessible label when the mark stands alone. Ignored when `decorative`. */
  title?: string;
  /** Hide from the a11y tree — use when an adjacent wordmark already labels it. */
  decorative?: boolean;
}) {
  return (
    <Image
      data-testid="atriam-mark"
      src={ATRIAM_ICON}
      alt={decorative ? "" : title}
      width={192}
      height={192}
      className={cn("h-7 w-7 shrink-0", className)}
      {...(decorative ? { "aria-hidden": true } : {})}
    />
  );
}

/** The Atriam column glyph used by every brand lockup. */
export function BrandMark({
  className,
  title = PRODUCT_DEF.branding.wordmark,
  decorative = false,
}: {
  className?: string;
  title?: string;
  decorative?: boolean;
}) {
  return (
    <AtriamMark className={className} title={title} decorative={decorative} />
  );
}

interface AtriamLogoProps extends ComponentPropsWithoutRef<"span"> {
  /** "full" = mark + wordmark, "mark" = glyph only, "wordmark" = text only. */
  variant?: "full" | "mark" | "wordmark";
  /** Extra classes for the glyph tile (e.g. sizing: `h-6 w-6`). */
  markClassName?: string;
  /** Extra classes for the wordmark text (e.g. `text-lg`). Color inherits. */
  wordmarkClassName?: string;
}

/** Unified Atriam logo used by the app, marketing, auth, and public surfaces. */
export function AtriamLogo({
  variant = "full",
  className,
  markClassName,
  wordmarkClassName,
  ...props
}: AtriamLogoProps) {
  const isFull = variant === "full";
  const wordmark = PRODUCT_DEF.branding.wordmark;
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      {...(isFull ? { role: "img", "aria-label": wordmark } : {})}
      {...props}
    >
      {variant !== "wordmark" && (
        <BrandMark className={markClassName} decorative={isFull} />
      )}
      {variant !== "mark" && (
        <span
          aria-hidden={isFull || undefined}
          className={cn("font-semibold tracking-tight", wordmarkClassName)}
        >
          {wordmark}
        </span>
      )}
    </span>
  );
}
